import {
  ACTIVE_STATUS,
  XmaxOpenClient,
  XMAX_OPEN_API_PRODUCTION_BASE_URL,
} from '../api/client'
import {
  createChangeConditionRtcRoomEvent,
  createStartRtcRoomEvent,
  createStopRtcRoomEvent,
  createTaskUid,
  createTracksRtcRoomEvent,
} from '../rtc/rtc-events'
import {
  RtcManager,
  type ExternalVideoPublishDeadlines,
  type RtcAsyncErrorInfo,
} from '../rtc/rtc-manager'
import { waitForCameraTrackReady } from '../rtc/media/camera-frame-readiness'
import { isAppleMobileRuntime } from '../rtc/media/mobile-runtime'
import { createDragTrackController, type DragTrackController } from '../drag/drag-track-controller'
import { createRemoteViewHost, type RemoteViewHost } from '../drag/remote-view-host'
import { createSDKI18n } from '../i18n'
import { notifyError, type XmaxErrorNotifier } from '../shared/error-utils'
import {
  createLowInputResolutionNotice,
  notifyNotice,
  type XmaxNoticeNotifier,
} from '../shared/sdk-notice'
import { XmaxSdkError, attachPublicErrorDetails, toXmaxSdkError } from '../shared/sdk-error'
import { createErrorDetails, type XmaxErrorDetails } from '../shared/error-details'
import { findUploadedAsset } from '../telemetry/assets'
import type { XmaxTelemetryParams } from '../telemetry/collector'
import {
  diagnosticError,
  disconnectReasonForStage,
  STARTUP_TRACE,
  type StartupStage,
  type StartupTrace,
  type WithStartupTrace,
  type XmaxDisconnectReasonCode,
} from '../telemetry/startup'
import { resolveCameraRtcEncoderSize } from './camera'
import { mountLocalStreamPreview } from './local-stream-preview'
import { DEFAULT_PRESENTATION_STATE, InstantPresentation, isInstantPrompt } from './instant'
import { writeRtcLogToConsole } from './rtc-console-log'
import type { XmaxClientOptions } from '../client/types'
import type {
  RealtimeConnectOptions,
  RealtimeContext,
  RealtimeDisconnectReason,
  RealtimeDragSetting,
  RealtimeMedia,
  RealtimeMediaKind,
  RealtimeMediaSetting,
  RealtimeRoomEventPayload,
  RealtimeRemoteVideoFirstFrameInfo,
  RealtimeSessionState,
  RealtimePresentationState,
} from './types'
import {
  isPositiveFinite,
  normalizeRealtimeContext,
  readTrackMediaSetting,
  resolveRealtimeStreamSetting,
} from './utils'
import {
  DEFAULT_REALTIME_AUTO_START,
  DEFAULT_REALTIME_HEARTBEAT_INTERVAL_MS,
  DEFAULT_REALTIME_RENDER_SETTING,
  DEFAULT_REALTIME_START_EVENT_MODEL,
} from './default'
import {
  APPLE_MOBILE_VIDEO_CONNECT_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_ENCODER_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_JOIN_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_OUTBOUND_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_PUBLISH_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_SOURCE_TIMEOUT_MS,
  APPLE_MOBILE_VIDEO_START_SEND_TIMEOUT_MS,
  withAppleMobileVideoStageTimeout,
} from './mobile-video-publish-compat'

const ENABLE_REALTIME_TIMING_LOGS = false

export type InternalRealtimeDisconnectReason =
  | 'client_disconnect'
  | 'client_background_timeout'
  | 'client_session_replaced'
  | 'client_context_disposed'
  | 'pagehide'
  | 'session_error'

/**
 * @brief 已建立的实时生成会话。
 * Session 同时管理生成状态、上下文、媒体输入和 RTC 生命周期。
 * {@link RealtimeSession.stopGeneration} 只停止生成并保留 RTC；{@link RealtimeSession.disconnect} 会彻底释放会话。
 */
export interface RealtimeSession {

  /** 当前生成生命周期状态。 */
  readonly state: RealtimeSessionState

  /** 最近一次成功应用的生成上下文。返回对象只读。 */
  readonly context: Readonly<RealtimeContext>

  /** 当前发布的输入流及其媒体规格。返回对象只读。 */
  readonly media: Readonly<RealtimeMedia>

  /** Current display state, including the input image used by Instant. */
  readonly presentation: Readonly<RealtimePresentationState>

  /** Custom drag surfaces call these at stroke boundaries; SDK-managed surfaces call them automatically. */
  beginDrag: () => void
  endDrag: () => void

  /**
   * @brief 获取服务端会话 ID。
   * @returns 活跃会话的 ID；断开后返回 `null`。
   */
  getSessionUid: () => string | null

  /**
   * @brief 应用生成上下文。
   * @param context 生成上下文；`prompt` 必填，`refImageUrl: null` 表示清除参考图。
   * @returns 应用完成后 resolve。
   * @notes 运行中发送 `change_condition`；进入、退出或更新 Instant 时发送 `start`，以应用静态图片参数。
   */
  set: (context: RealtimeContext) => Promise<void>

  /**
   * @brief 强制创建新的生成任务。
   * @param context 可选的生成上下文；传入时 `prompt` 必填，并与当前上下文合并。
   * @returns Start 事件发送并完成本地启动流程后 resolve。
   * @notes 无论当前是否正在运行，每次调用都会发送 `start`。
   */
  start: (context?: RealtimeContext) => Promise<void>

  /**
   * @brief 发送一组拖拽轨迹点。
   * @param tracks 轨迹点数组，每个点为 `[x, y]`，坐标基于 `media.streamSetting` 的像素坐标系。
   * @returns 轨迹消息提交后 resolve；当前没有活动任务时直接 resolve。
   */
  sendTracks: (tracks: Array<[number, number]>) => Promise<void>

  /**
   * @brief 停止当前生成任务。
   * @returns Stop 事件处理完成后 resolve。
   * @notes RTC 房间和输入流保持连接，可再次调用 start。
   */
  stopGeneration: () => Promise<void>

  /**
   * @brief 停止当前会话的本地输入流，同时保持 RTC 房间连接。
   * @returns 本地采集、预览与发布停止后 resolve。
   * @notes 当前用于开放平台在停止生成后立即关闭本地输入，同时保持 RTC 房间连接，
   * 以等待 `video_completed` 等最终事件并向用户提供视频保存。
   * SDK 管理的输入资源会被释放；调用方传入的原始 Track 不会被停止。
   * 当前尚未提供在同一会话中恢复本地 Stream 的方法，请谨慎调用；完成结果处理后通常应调用 `disconnect()`。
   */
  stopLocalStream: () => Promise<void>

  /**
   * @brief 彻底断开实时会话。
   * @returns RTC 离房、服务端会话关闭及 SDK 托管资源释放完成后 resolve。
   * @notes 调用方通过 connect 传入的 MediaStream 不会由 SDK 停止。
   */
  disconnect: () => Promise<void>
}

function getVideoTrack(stream: MediaStream): MediaStreamTrack {
  const track = stream.getVideoTracks()[0]
  if (!track) {
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', 'Missing video track in MediaStream')
  }
  return track
}

class SessionBootstrapCancelledError extends XmaxSdkError {
  constructor() {
    super('SESSION_ERROR', 'Realtime bootstrap was cancelled')
    this.name = 'SessionBootstrapCancelledError'
  }
}

function getAudioTrack(stream: MediaStream): MediaStreamTrack | undefined {
  return stream.getAudioTracks().find((track) => track.readyState === 'live')
}

function createManagedLocalVideoHost(wrapper: HTMLElement): HTMLElement {
  const computed = getComputedStyle(wrapper)
  if (computed.position === 'static') {
    wrapper.style.position = 'relative'
  }
  wrapper.style.overflow = 'hidden'

  const host = document.createElement('div')
  host.setAttribute('data-xmax-local-video', 'true')
  host.style.cssText =
    'position:relative;width:100%;height:100%;overflow:hidden;display:flex;align-items:center;justify-content:center;'
  wrapper.replaceChildren(host)
  return host
}

function observeManagedVideoStyle(
  host: HTMLElement,
  fit: 'cover' | 'contain',
  mirrored: boolean,
): () => void {
  host.style.transform = mirrored ? 'scaleX(-1)' : 'none'
  host.style.transformOrigin = 'center'

  const apply = () => {
    host.querySelectorAll('video, canvas').forEach((node) => {
      const element = node as HTMLElement
      element.style.width = '100%'
      element.style.height = '100%'
      element.style.objectFit = fit
      element.style.objectPosition = 'center'
    })
  }
  apply()
  const MutationObserverCtor = globalThis.MutationObserver
  if (!MutationObserverCtor) {
    return () => {}
  }
  const observer = new MutationObserverCtor(apply)
  observer.observe(host, { childList: true, subtree: true })
  return () => observer.disconnect()
}

export type PreparedRealtimeMedia = {
  kind: RealtimeMediaKind
  /** Reuse the composited input image; its lifetime belongs to the prepared media. */
  posterUrl?: string
  /** @internal Original source kind used only by non-fatal input notices. */
  noticeInputKind?: RealtimeMediaKind
  stream: MediaStream
  sourceSetting?: RealtimeMediaSetting
  publishTrack?: MediaStreamTrack
  publishAudioTrack?: MediaStreamTrack
  /** Volcengine-managed camera capture; absent for caller-provided tracks and media files. */
  internalCamera?: {
    facingMode?: 'user' | 'environment'
    withAudio?: boolean
    mirrorInput?: boolean
  }
  /** SDK-owned media is disposed with the session. */
  owned?: boolean
  cleanup?: () => void
  /** @internal Limit the WebKit publish recovery path to local video files. */
  appleMobileVideoFileCompatibility?: boolean
}

export type InternalRealtimeStartOptions = {
  preserveRemoteOutput?: boolean
}

const PREPARED_REALTIME_MEDIA = Symbol('preparedRealtimeMedia')
const LOCAL_CAMERA_PREVIEW_REVEAL_MS = 300

function isLikelyMobileRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod|Android/i.test(ua)) {
    return true
  }
  return navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)
}

type InternalRealtimeConnectOptions = RealtimeConnectOptions & {
  [PREPARED_REALTIME_MEDIA]?: PreparedRealtimeMedia
}

function probeDisconnectReason(reason: RealtimeDisconnectReason): XmaxDisconnectReasonCode {
  if (reason === 'client') return 'client_disconnect'
  if (reason === 'error') return 'session_error'
  if (reason === 'overloaded') return 'remote_output_stalled'
  return reason
}

function localOutboundDisconnectReason(
  reason: 'zero-sent-frame-rate' | 'track-ended' | 'track-muted',
): XmaxDisconnectReasonCode {
  if (reason === 'track-ended') return 'local_outbound_track_ended'
  if (reason === 'track-muted') return 'local_outbound_track_muted'
  return 'local_outbound_zero_sent_frame_rate'
}

/** @internal Attach an SDK-owned prepared input without expanding the public connect options. */
export function withPreparedRealtimeMedia(
  options: RealtimeConnectOptions,
  prepared: PreparedRealtimeMedia,
): RealtimeConnectOptions {
  return Object.assign({}, options, { [PREPARED_REALTIME_MEDIA]: prepared })
}

export class RealtimeSessionImpl implements RealtimeSession {
  private readonly startup?: StartupTrace
  private readonly client: XmaxOpenClient
  private readonly rtc: RtcManager
  private readonly reportError: (error: unknown, fallbackMessage?: string) => never
  private readonly notifyUserError: XmaxErrorNotifier | undefined
  private readonly notifyUserNotice: XmaxNoticeNotifier | undefined
  private readonly sdkI18n: ReturnType<typeof createSDKI18n>
  private readonly localElement: HTMLElement | null
  private readonly remoteElement: HTMLElement | null
  private readonly onRemoteStream?: (stream: MediaStream) => void
  private readonly onRemoteVideoFirstFrame?: (info: RealtimeRemoteVideoFirstFrameInfo) => void
  private readonly onDisconnect?: RealtimeConnectOptions['onDisconnect']
  private disconnectDetails?: XmaxErrorDetails
  private operationStage?: StartupStage
  private readonly onStateChange?: (state: RealtimeSessionState) => void
  private readonly onPublishPipelineWaitChange?: (waiting: boolean) => void
  private readonly onRoomEvent?: (event: RealtimeRoomEventPayload) => void
  private readonly rtcLogEnabled: boolean
  private readonly ownsMedia: boolean
  private readonly cleanupMedia?: () => void
  private readonly localRenderMirror: boolean
  private readonly renderCleanup: Array<() => void> = []

  private mediaValue: Readonly<RealtimeMedia>

  get media(): Readonly<RealtimeMedia> {
    return this.mediaValue
  }

  private sessionUid: string | null = null
  private taskUid: string | null = null
  private currentState: RealtimeSessionState = 'idle'
  private currentContext: Readonly<RealtimeContext> = Object.freeze({ prompt: '' })
  private runningContext: Readonly<RealtimeContext> | null = null
  private disconnected = false
  /** True only after `connect()` bootstrap succeeds — gates `onDisconnect`. */
  private sessionEstablished = false
  private disconnectNotified = false
  private mediaCleaned = false
  private forceAbortTask: Promise<void> | null = null
  private heartbeatStarted = false
  private remoteFirstFrameNotified = false
  private lowInputResolutionNotified = false
  private initialRemoteFramePending = false
  private fatalRtcError: Error | null = null
  private heartbeatIntervalMs = DEFAULT_REALTIME_HEARTBEAT_INTERVAL_MS
  private pendingDisconnectReason: RealtimeDisconnectReason = 'client'
  private remoteViewHost: RemoteViewHost | null = null
  private dragController: DragTrackController | null = null
  private dragTrackFitMode: 'contain' | 'cover' = 'cover'
  private hiddenRemoteElement: HTMLElement | null = null
  private remoteStreamNotifyTimer: ReturnType<typeof setInterval> | null = null
  private lastNotifiedRemoteStream: MediaStream | null = null
  private readonly dragSetting: RealtimeDragSetting | undefined
  /** change_condition 固定使用首次 start 的分辨率，避免中途被替换。 */
  private firstStartEventSize: Readonly<[number, number]> | null = null
  /** Camera mirror state belongs to this Session/RTC room only. */
  private readonly shouldMirrorCameraInput: boolean
  private readonly appleMobileVideoUpload: boolean
  private readonly inputPosterUrl?: string
  private readonly inputKind: RealtimeMediaKind
  private readonly onPresentationChange?: RealtimeConnectOptions['onPresentationChange']
  private readonly inputImageTransitionMs?: number
  private instant: InstantPresentation | null = null
  private instantAsset: { source: string; url: Promise<string> } | null = null
  private instantUploadAbort: AbortController | null = null
  private generationRevision = 0

  private constructor(
    client: XmaxOpenClient,
    rtc: RtcManager,
    reportError: (error: unknown, fallbackMessage?: string) => never,
    options: RealtimeConnectOptions,
    media: RealtimeMedia,
    prepared: PreparedRealtimeMedia,
    clientOptions?: Pick<XmaxClientOptions, 'onNotice' | 'onPublishPipelineWaitChange' | 'telemetry'>,
    notifyUserError?: XmaxErrorNotifier,
    sdkI18n?: ReturnType<typeof createSDKI18n>,
  ) {
    this.client = client
    this.startup = (options as WithStartupTrace)[STARTUP_TRACE]
    this.rtc = rtc
    this.reportError = reportError
    this.notifyUserError = notifyUserError
    this.notifyUserNotice = options.onNotice ?? clientOptions?.onNotice
    this.sdkI18n = sdkI18n ?? createSDKI18n()
    this.mediaValue = Object.freeze(media)
    const localContainer = options.render?.localContainer ?? null
    this.localElement = localContainer ? createManagedLocalVideoHost(localContainer) : null
    this.localRenderMirror = options.render?.mirror ?? DEFAULT_REALTIME_RENDER_SETTING.mirror
    this.onRemoteStream = options.onRemoteStream
    this.onRemoteVideoFirstFrame = options.onRemoteVideoFirstFrame
    this.onDisconnect = options.onDisconnect
    this.onStateChange = options.onStateChange
    this.onPublishPipelineWaitChange = clientOptions?.onPublishPipelineWaitChange
    this.onRoomEvent = options.onRoomEvent
    this.rtcLogEnabled = options.log?.rtc === true
    this.ownsMedia = prepared.owned === true
    this.appleMobileVideoUpload = prepared.kind === 'video'
      && prepared.appleMobileVideoFileCompatibility === true
      && isAppleMobileRuntime()
    this.shouldMirrorCameraInput = prepared.internalCamera?.mirrorInput === true
    this.cleanupMedia = prepared.cleanup
    this.inputPosterUrl = prepared.posterUrl
    this.inputKind = prepared.noticeInputKind ?? prepared.kind
    this.onPresentationChange = options.onPresentationChange
    this.inputImageTransitionMs = options.render?.inputImageTransitionMs
    this.dragTrackFitMode = options.render?.fit
      ?? (media.kind === 'image' || media.kind === 'video' ? 'contain' : 'cover')
    this.dragSetting = options.render?.drag

    if (this.localElement && !prepared.internalCamera) {
      this.renderCleanup.push(
        mountLocalStreamPreview(this.localElement, prepared.stream, this.dragTrackFitMode),
      )
    }

    this.rtc.setOnRemoteOutputStalled((info) => {
      this.failOverloaded(info.reason && info.reason !== 'remote_stats_timeout' ? info.reason : 'remote_output_stalled', {
        detection_source: 'render_frame_watch', ...info.diagnostics,
        remote_user_id: info.userId, stall_ms: info.stallMs,
      })
    })
    this.rtc.setOnError((error) => this.handleRtcAsyncError(error))
    this.rtc.setOnRoomEvent((event) => {
      this.onRoomEvent?.(event as RealtimeRoomEventPayload)
    })
    this.rtc.setOnRemoteVideoFirstFrame((info) => this.handleRemoteVideoFirstFrame(info))

    const remoteElement = options.render?.remoteContainer
    if (remoteElement) {
      this.remoteElement = remoteElement
      this.remoteViewHost = createRemoteViewHost(this.remoteElement)
      this.renderCleanup.push(observeManagedVideoStyle(
        this.remoteViewHost.videoHost,
        this.dragTrackFitMode,
        false,
      ))
      if (this.localElement) {
        this.renderCleanup.push(observeManagedVideoStyle(
          this.localElement,
          this.dragTrackFitMode,
          this.localRenderMirror,
        ))
      }
      return
    }

    if (options.onRemoteStream) {
      const hidden = document.createElement('div')
      hidden.setAttribute('data-Xmax-remote-host', 'true')
      hidden.style.cssText =
        'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden'
      document.body.appendChild(hidden)
      this.hiddenRemoteElement = hidden
      this.remoteElement = hidden
      this.remoteViewHost = createRemoteViewHost(hidden)
      if (this.localElement) {
        this.renderCleanup.push(observeManagedVideoStyle(
          this.localElement,
          this.dragTrackFitMode,
          this.localRenderMirror,
        ))
      }
      this.rtc.setOnRemotePlayerMounted(() => {
        this.scheduleNotifyRemoteStream()
      })
      return
    }

    this.remoteElement = null
    if (this.localElement) {
      this.renderCleanup.push(observeManagedVideoStyle(
        this.localElement,
        this.dragTrackFitMode,
        this.localRenderMirror,
      ))
    }
  }

  private get remoteVideoContainer(): HTMLElement | null {
    return this.remoteViewHost?.videoHost ?? this.remoteElement
  }

  get state(): RealtimeSessionState {
    return this.currentState
  }

  get context(): Readonly<RealtimeContext> {
    return this.currentContext
  }

  get presentation(): Readonly<RealtimePresentationState> {
    return this.instant?.state ?? DEFAULT_PRESENTATION_STATE
  }

  beginDrag(): void { this.instant?.beginDrag() }
  endDrag(): void { this.instant?.endDrag() }

  private instantImage(context: RealtimeContext): string | null {
    if (!isInstantPrompt(context.prompt)) return null
    if (this.inputKind === 'image' && !this.inputPosterUrl) {
      throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', 'Failed to prepare Instant input image')
    }
    return this.inputPosterUrl || context.refImageUrl?.trim() || null
  }

  private prepareInstantImage(source: string): Promise<string> {
    if (this.instantAsset?.source === source) return this.instantAsset.url
    this.instantUploadAbort?.abort()
    const abort = new AbortController()
    this.instantUploadAbort = abort
    const url = (async () => {
      // An explicit remote reference can be sent directly. Prepared posters are uploaded once.
      if (/^https?:\/\//i.test(source)) return source
      const response = await fetch(source, { signal: abort.signal })
      if (!response.ok) throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', 'Failed to read Instant input image')
      const blob = await response.blob()
      const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg'
      const result = await this.client.uploadImage(
        new File([blob], `instant-input.${extension}`, { type: blob.type || 'image/jpeg' }),
        { signal: abort.signal },
      )
      return result.url
    })()
    this.instantAsset = { source, url }
    void url.then(() => {
      if (this.instantUploadAbort === abort) this.instantUploadAbort = null
    }, () => {
      if (this.instantUploadAbort === abort) {
        this.instantAsset = null
        this.instantUploadAbort = null
      }
    })
    return url
  }

  private cancelInstant(): void {
    this.generationRevision = (this.generationRevision ?? 0) + 1
    this.instant?.reset()
    if (this.instantUploadAbort) {
      this.instantUploadAbort.abort()
      this.instantUploadAbort = null
      this.instantAsset = null
    }
  }

  private logTiming(
    stage: string,
    startedAt: number,
    data?: Record<string, unknown>,
  ): void {
    if (!ENABLE_REALTIME_TIMING_LOGS || !this.rtcLogEnabled) {
      return
    }
    console.info(`[Realtime Timing] ${stage}`, {
      elapsedMs: Number((performance.now() - startedAt).toFixed(1)),
      ...data,
    })
  }

  private hideLocalCameraPreview(): void {
    if (!this.localElement) {
      return
    }
    this.localElement.style.transition = 'none'
    this.localElement.style.opacity = '0'
  }

  private revealLocalCameraPreview(): void {
    if (!this.localElement) {
      return
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    this.localElement.style.transition = reduceMotion
      ? 'none'
      : `opacity ${LOCAL_CAMERA_PREVIEW_REVEAL_MS}ms ease-out`
    this.localElement.style.opacity = '1'
  }

  private async measureTiming<T>(
    stage: string,
    operation: () => Promise<T>,
    data?: Record<string, unknown>,
  ): Promise<T> {
    const startedAt = performance.now()
    try {
      const result = await operation()
      this.logTiming(stage, startedAt, data)
      return result
    } catch (error) {
      this.logTiming(stage, startedAt, { ...data, failed: true })
      throw error
    }
  }

  private runAppleMobileVideoStage<T>(
    operation: () => Promise<T>,
    options: {
      stage: string
      timeoutMs: number
    },
  ): Promise<T> {
    if (!this.appleMobileVideoUpload) {
      return operation()
    }
    return withAppleMobileVideoStageTimeout(operation, {
      ...options,
      message: this.sdkI18n.t('errors.systemOverloaded'),
    })
  }

  private measureStartup<T>(stage: StartupStage, operation: () => Promise<T>): Promise<T> {
    this.operationStage = stage
    const context = { failure_stage: stage, generation_id: this.taskUid ?? null, session_id: this.sessionUid ?? null }
    return (this.startup ? this.startup.measure(stage, operation) : operation()).catch(error => {
      throw this.withErrorDetails(error, disconnectReasonForStage(stage), context)
    })
  }

  private diagnosticDetails(reason: XmaxDisconnectReasonCode, evidence: XmaxTelemetryParams = {}): XmaxErrorDetails {
    return createErrorDetails({
      reason_code: reason,
      failure_stage: this.operationStage ?? this.startup?.currentStage,
      trace_id: this.startup?.traceId,
      session_id: this.sessionUid,
      generation_id: this.taskUid,
      ...this.rtc?.getConnectionDiagnostics?.(),
      ...evidence,
    })
  }

  private withErrorDetails(error: unknown, reason: XmaxDisconnectReasonCode, evidence: XmaxTelemetryParams = {}): XmaxSdkError {
    const resolved = toXmaxSdkError(error, { code: 'UNKNOWN_ERROR', message: this.sdkI18n?.t('errors.requestFailed') ?? 'Realtime operation failed' })
    return attachPublicErrorDetails(resolved, this.diagnosticDetails(reason, { ...diagnosticError(resolved), ...evidence }))
  }

  private async ensureBootstrapActive(): Promise<void> {
    if (!this.appleMobileVideoUpload || !this.disconnected) {
      return
    }
    const activeSessionUid = this.sessionUid
    this.sessionUid = null
    if (activeSessionUid) {
      this.client.beginTeardown()
      try {
        await this.client.closeSession(activeSessionUid)
      } catch {
      } finally {
        this.client.endTeardown()
      }
    }
    throw new SessionBootstrapCancelledError()
  }

  private cleanupForceAbortedMedia(): boolean {
    if (this.mediaCleaned) {
      return true
    }
    this.mediaCleaned = true
    try {
      this.cleanupMedia?.()
      return true
    } catch {
      return false
    }
  }

  private forceAbortBootstrap(
    failureReason: XmaxDisconnectReasonCode,
    diagnostics: XmaxTelemetryParams = {},
  ): Promise<void> {
    this.cancelInstant()
    const startedAt = performance.now()
    this.disconnectDetails ??= this.diagnosticDetails(failureReason, diagnostics)
    const { reason_code: _reason, trace_id: _trace, session_id: _session, generation_id: _generation, ...details } = this.disconnectDetails
    diagnostics = { ...diagnostics, ...details }
    this.startup?.recordError({ ...diagnostics, failure_reason: failureReason })
    const mediaCleanupSucceeded = this.cleanupForceAbortedMedia()
    if (this.forceAbortTask) {
      return this.forceAbortTask
    }
    this.disconnected = true
    this.initialRemoteFramePending = false
    const activeSessionUid = this.sessionUid
    this.sessionUid = null
    const generationStopAttempted = Boolean(this.taskUid && this.rtc.snapshot.joined)

    if (generationStopAttempted) {
      void this.rtc.sendRoomEvent(
        createStopRtcRoomEvent({
          userId: this.rtc.snapshot.userId ?? undefined,
          sessionUid: this.taskUid ?? undefined,
        }),
      ).catch(() => undefined)
      this.startup?.setTerminalContext({
        generation_stop_called: true,
        generation_stop_outcome: 'best_effort_dispatched',
        generation_stop_trigger: 'bootstrap_abort',
        generation_stop_offset_ms: this.startup.offset(),
      })
    }

    this.rtc.forceDestroy({ preserveExternalTrack: !this.ownsMedia })
    this.startup?.setTerminalContext({
      local_stream_stop_called: true,
      local_stream_stop_outcome: 'forced',
      local_stream_stop_trigger: 'bootstrap_abort',
      local_stream_stop_offset_ms: this.startup.offset(),
      local_stream_media_cleanup_succeeded: mediaCleanupSucceeded,
    })
    this.clearRemoteStreamNotifyTimer()
    this.lastNotifiedRemoteStream = null
    try {
      this.dragController?.destroy()
    } catch {
      // Forced teardown must continue to its terminal telemetry event.
    }
    this.dragController = null
    this.renderCleanup.splice(0).forEach((cleanup) => {
      try { cleanup() } catch { /* Forced teardown continues. */ }
    })
    if (this.hiddenRemoteElement) {
      try { this.hiddenRemoteElement.remove() } catch { /* Forced teardown continues. */ }
      this.hiddenRemoteElement = null
    }

    this.runningContext = null
    this.taskUid = null
    this.setState('disconnected')
    this.emitDisconnect('error')

    this.client.beginTeardown()
    this.forceAbortTask = (async (): Promise<void> => {
      let serverCloseConfirmed = false
      if (activeSessionUid) {
        try {
          serverCloseConfirmed = (await this.client.closeSession(activeSessionUid)) !== null
        } catch {
          serverCloseConfirmed = false
        }
      }
      this.startup?.finish('error', {
        ...diagnostics,
        failure_reason: failureReason,
        close_mode: 'forced',
        generation_stop_attempted: generationStopAttempted,
        generation_stop_succeeded: undefined,
        local_publish_stop_attempted: true,
        local_publish_stop_succeeded: undefined,
        rtc_leave_attempted: true,
        rtc_leave_succeeded: undefined,
        server_close_attempted: Boolean(activeSessionUid),
        server_close_confirmed: serverCloseConfirmed,
        media_cleanup_succeeded: mediaCleanupSucceeded,
        duration_ms: Math.round(performance.now() - startedAt),
      })
    })().finally(() => {
      this.client.endTeardown()
    })
    return this.forceAbortTask
  }

  static async connect(
    stream: MediaStream,
    options: RealtimeConnectOptions,
    clientOptions: XmaxClientOptions,
  ): Promise<RealtimeSessionImpl> {
    const connectStartedAt = performance.now()
    const trace = (options as WithStartupTrace)[STARTUP_TRACE]
    trace?.complete('media_prepare', trace.startedAt)
    const internalOptions = options as InternalRealtimeConnectOptions
    const internalPrepared = internalOptions[PREPARED_REALTIME_MEDIA]
    const prepared: PreparedRealtimeMedia = internalPrepared
      ?? {
          kind: 'stream',
          stream,
          publishTrack: getVideoTrack(stream),
          publishAudioTrack: getAudioTrack(stream),
          owned: false,
        }

    const sdkI18n = clientOptions.i18n ?? createSDKI18n({ locale: clientOptions.locale })
    // Connect-level onError wins when set; otherwise keep createXmaxClient.onError.
    const userErrorCallback = options.onError ?? clientOptions.onError
    const notifyUserError: XmaxErrorNotifier = (message, error) => {
      if (session) session.withErrorDetails(error, disconnectReasonForStage(session.operationStage ?? trace?.currentStage))
      userErrorCallback?.(message, error)
    }
    const reportError = (error: unknown, fallbackMessage?: string): never => {
      throw notifyError(error, notifyUserError, {
        code: 'UNKNOWN_ERROR',
        message: fallbackMessage ?? sdkI18n.t('errors.requestFailed'),
      })
    }

    const modelName = options.model.name.trim()
    if (!modelName) {
      prepared.cleanup?.()
      throw new XmaxSdkError('INVALID_MODEL', 'model.name is required')
    }
    let session: RealtimeSessionImpl
    const client = new XmaxOpenClient({
      apiKey: clientOptions.apiKey,
      authToken: clientOptions.authToken,
      baseUrl: clientOptions.baseUrl ?? XMAX_OPEN_API_PRODUCTION_BASE_URL,
      i18n: sdkI18n,
      onError: notifyUserError,
      telemetry: clientOptions.telemetry,
      reportSource: clientOptions.reportSource,
      telemetryContext: () => session?.startup?.params ?? trace?.params,
      telemetryRecord: (name, params) => (session?.startup ?? trace)?.record(name, params),
    })

    const rtcLogEnabled = options.log?.rtc === true
    const rtc = new RtcManager({
      diagnostics: trace,
      renderFit: options.render?.fit,
      playRemoteAudio: options.audio?.subscribe === true,
      onLog: rtcLogEnabled ? writeRtcLogToConsole : undefined,
      onRemoteSeiReceived: clientOptions.onRemoteSei,
      onSeiGateChange: state => {
        session?.instant?.onGate(state.matched)
        clientOptions.onSeiGateChange?.(state)
      },
      i18n: sdkI18n,
    })

    const publishTrack = prepared.internalCamera
      ? undefined
      : (prepared.publishTrack ?? getVideoTrack(prepared.stream))
    if (!prepared.sourceSetting && !publishTrack) {
      throw new XmaxSdkError('INVALID_INPUT', 'Missing media source setting')
    }
    const sourceSetting = prepared.sourceSetting ?? readTrackMediaSetting(publishTrack!)
    const streamSetting = resolveRealtimeStreamSetting(options, sourceSetting)
    const media: RealtimeMedia = Object.freeze({
      kind: prepared.kind,
      stream: prepared.stream,
      sourceSetting: Object.freeze({ ...sourceSetting }),
      streamSetting,
    })

    if (prepared.internalCamera && rtcLogEnabled) {
      writeRtcLogToConsole({
        level: 'info',
        message: '[Xmax][RTC] connection options',
        data: {
          facingMode: prepared.internalCamera.facingMode,
          stream: streamSetting,
        },
      })
    }

    session = new RealtimeSessionImpl(
      client,
      rtc,
      reportError,
      options,
      media,
      prepared,
      clientOptions,
      notifyUserError,
      sdkI18n,
    )

    if (!prepared.internalCamera) {
      session.notifyLowInputResolution(
        prepared.noticeInputKind ?? prepared.kind,
        sourceSetting,
      )
    }

    try {
      const instantSource = session.instantImage(options.context ?? { prompt: '' })
      if (instantSource) await session.prepareInstantImage(instantSource)
      await session.runAppleMobileVideoStage(
        () => session.bootstrap(
          prepared,
          options,
          modelName,
          clientOptions.heartbeatIntervalMs ?? DEFAULT_REALTIME_HEARTBEAT_INTERVAL_MS,
        ),
        {
          stage: 'realtime_connect',
          timeoutMs: APPLE_MOBILE_VIDEO_CONNECT_TIMEOUT_MS,
        },
      )
      if (session.fatalRtcError) {
        throw session.fatalRtcError
      }
      session.sessionEstablished = true
      trace?.markConnected()
      session.logTiming('SDK 实时连接总耗时', connectStartedAt, {
        mediaKind: prepared.kind,
      })
      return session
    } catch (error) {
      session.logTiming('SDK 实时连接总耗时', connectStartedAt, {
        mediaKind: prepared.kind,
        failed: true,
      })
      const failureReason = (error instanceof XmaxSdkError ? error.details?.reason_code : undefined)
        ?? disconnectReasonForStage(session.operationStage ?? trace?.currentStage)
      error = session.withErrorDetails(error, failureReason)
      const diagnostics = {
        failure_stage: trace?.currentStage,
        ...diagnosticError(error),
      }
      if (session.appleMobileVideoUpload) {
        await session.forceAbortBootstrap(failureReason, diagnostics)
      } else if (!session.disconnected) {
        await session.safeDisconnect({
          reason: 'error',
          failureReason,
          diagnostics,
        })
      }
      throw reportError(error)
    }
  }

  /** Tear down the session when outbound or first remote frames never become ready. */
  private failOverloaded(cause: XmaxDisconnectReasonCode, evidence: XmaxTelemetryParams = {}): void {
    if (this.disconnected) {
      return
    }
    const message = this.sdkI18n.t(cause === 'remote_first_frame_timeout' ? 'errors.systemOverloaded' : 'errors.rtcStreamInterrupted')
    const error = new XmaxSdkError('WEB_RTC_ERROR', message)
    const diagnostics = {
      failure_stage: cause === 'remote_first_frame_timeout' || this.initialRemoteFramePending ? 'wait_first_frame' : 'streaming',
      error_code: error.code,
      ...this.startup?.rtcEvidence(),
      ...evidence,
    }
    this.withErrorDetails(error, cause, diagnostics)
    this.startup?.recordError({ ...diagnostics, failure_reason: cause })
    if (this.appleMobileVideoUpload) {
      void this.forceAbortBootstrap(cause, diagnostics)
    } else {
      void this.safeDisconnect({ reason: 'overloaded', failureReason: cause, diagnostics })
    }
    try {
      notifyError(error, this.notifyUserError, message)
    } catch {
      // Consumer error callbacks must not prevent Session teardown.
    }
  }

  /** Forward Volcengine's unrecoverable asynchronous RTC errors, then close the Session. */
  private handleRtcAsyncError(info: RtcAsyncErrorInfo): void {
    if (this.disconnected) {
      return
    }
    const message = this.sdkI18n.t('rtc.error', { code: info.errorCode })
    const error = new XmaxSdkError('WEB_RTC_ERROR', message)
    this.fatalRtcError = error
    const diagnostics = {
      failure_stage: 'rtc',
      rtc_error_code: info.errorCode,
      error_code: error.code,
    }
    this.withErrorDetails(error, 'rtc_async_error', diagnostics)
    this.startup?.recordError({ ...diagnostics, failure_reason: 'rtc_async_error' })
    if (this.appleMobileVideoUpload) {
      void this.forceAbortBootstrap('rtc_async_error', diagnostics)
    } else {
      void this.safeDisconnect({ reason: 'error', failureReason: 'rtc_async_error', diagnostics })
    }
    try {
      notifyError(error, this.notifyUserError, message)
    } catch {
      // Consumer error callbacks must not prevent Session teardown.
    }
  }

  /**
   * Any failure that blocks output: send stop (if generation active), leave RTC,
   * close session, then fire `onDisconnect`.
   */
  private failOutputAndDisconnect(
    error: unknown,
    failureReason?: XmaxDisconnectReasonCode,
    operationContext: XmaxTelemetryParams = {},
  ): Promise<never> | never {
    const original = error instanceof XmaxSdkError ? error.details : undefined
    failureReason ??= original?.reason_code ?? disconnectReasonForStage(this.operationStage ?? this.startup?.currentStage)
    const diagnostics = {
      failure_stage: original?.failure_stage ?? this.operationStage ?? this.startup?.currentStage,
      ...diagnosticError(error),
      ...operationContext,
    }
    error = this.withErrorDetails(error, failureReason, diagnostics)
    this.startup?.recordError({ ...diagnostics, failure_reason: failureReason })
    if (this.appleMobileVideoUpload) {
      return this.failAppleMobileVideoAndDisconnect(error, failureReason, diagnostics)
    }
    if (!this.disconnected) {
      void this.safeDisconnect({ reason: 'error', failureReason, diagnostics })
    }
    this.reportError(error)
  }

  private async failAppleMobileVideoAndDisconnect(
    error: unknown,
    failureReason: XmaxDisconnectReasonCode,
    diagnostics: XmaxTelemetryParams = {},
  ): Promise<never> {
    await this.forceAbortBootstrap(failureReason, diagnostics)
    this.reportError(error)
  }

  private async bootstrap(
    prepared: PreparedRealtimeMedia,
    options: RealtimeConnectOptions,
    modelName: string,
    heartbeatIntervalMs: number,
  ): Promise<void> {
    const createdSession = await this.measureStartup(
      'session_api',
      () => this.client.createSession(modelName),
    )
    if (!createdSession.sessionUid?.trim()) {
      throw new XmaxSdkError('SESSION_ERROR', 'Session uid is missing')
    }

    this.sessionUid = createdSession.sessionUid
    this.startup?.setContext({
      session_id: this.sessionUid,
      stream_width: this.media.streamSetting.width,
      stream_height: this.media.streamSetting.height,
      stream_fps: this.media.streamSetting.fps,
    })
    if (this.appleMobileVideoUpload) {
      await this.ensureBootstrapActive()
    }
    this.heartbeatIntervalMs = heartbeatIntervalMs

    const joinInfo = this.client.getRtcJoinInfo(createdSession)
    this.startup?.setContext({ rtc_room_id: joinInfo.roomId })
    if (prepared.internalCamera) {
      const internalCamera = prepared.internalCamera
      const encoderSize = resolveCameraRtcEncoderSize(this.media.streamSetting)
      this.hideLocalCameraPreview()
      await this.measureStartup('rtc_prepare', () => this.rtc.prepareEngine(joinInfo))

      if (this.localElement) {
        this.rtc.setLocalVideoContainer(this.localElement)
      }
      if (this.remoteVideoContainer) {
        this.rtc.setRemoteVideoContainer(this.remoteVideoContainer)
      }

      const captureSettings = await this.measureStartup(
        'camera_capture',
        () => this.rtc.startInternalVideoCapture({
          encoderSize,
          encoderFps: this.media.streamSetting.fps,
          encoderMaxKbps: this.media.streamSetting.maxKbps,
          encoderContentHint: this.media.streamSetting.contentHint,
          facingMode: internalCamera.facingMode,
        }),
      )
      this.updateCameraSourceSetting(captureSettings, false)
      if (internalCamera.withAudio) {
        await this.measureStartup('camera_audio', () => this.rtc.startInternalAudioCapture())
      }

      await this.measureStartup('rtc_join', () => this.rtc.joinPreparedRoom())

      this.startup?.enter('camera_ready')
      const cameraTrack = this.rtc.getInternalVideoTrack()
      if (!cameraTrack) {
        throw new XmaxSdkError('CAMERA_ERROR', 'Missing internal camera track')
      }
      this.updateCameraSourceSetting(
        typeof cameraTrack.getSettings === 'function' ? cameraTrack.getSettings() : {},
      )
      const cameraReadyStartedAt = performance.now()
      const cameraReady = await this.measureStartup('camera_ready', () => waitForCameraTrackReady(cameraTrack, {
        consecutiveFrames: 2,
        timeoutMs: 1000,
      }))
      this.logTiming('等待摄像头画面就绪', cameraReadyStartedAt, cameraReady)
      this.revealLocalCameraPreview()

      const track = await this.measureStartup('publish', () => this.rtc.publishInternalVideo())
      prepared.stream.addTrack(track)
    } else {
      await this.runAppleMobileVideoStage(
        () => this.measureStartup('rtc_join', () => this.rtc.join(joinInfo)),
        {
          stage: 'rtc_join',
          timeoutMs: APPLE_MOBILE_VIDEO_JOIN_TIMEOUT_MS,
        },
      )
      if (this.appleMobileVideoUpload) {
        await this.ensureBootstrapActive()
      }

      if (this.remoteVideoContainer) {
        this.rtc.setRemoteVideoContainer(this.remoteVideoContainer)
      }

      const externalPublishDeadlines: ExternalVideoPublishDeadlines | undefined =
        this.appleMobileVideoUpload
          ? {
              externalSourceMs: APPLE_MOBILE_VIDEO_SOURCE_TIMEOUT_MS,
              encoderMs: APPLE_MOBILE_VIDEO_ENCODER_TIMEOUT_MS,
              publishMs: APPLE_MOBILE_VIDEO_PUBLISH_TIMEOUT_MS,
              timeoutMessage: this.sdkI18n.t('errors.systemOverloaded'),
            }
          : undefined
      await this.measureStartup(
        'publish',
        () => this.rtc.startExternalVideoPublishing(
          prepared.publishTrack ?? getVideoTrack(prepared.stream),
          [this.media.streamSetting.width, this.media.streamSetting.height],
          {
            highQuality: true,
            fps: this.media.streamSetting.fps,
            maxKbps: this.media.streamSetting.maxKbps,
            contentHint: this.media.streamSetting.contentHint,
            audioTrack: prepared.publishAudioTrack,
          },
          externalPublishDeadlines,
        ),
      )
      if (this.appleMobileVideoUpload) {
        await this.ensureBootstrapActive()
      }

      if (this.appleMobileVideoUpload) {
        await this.measureStartup('outbound_ready', () => this.rtc.waitForLocalOutboundVideoReady({
          timeoutMs: APPLE_MOBILE_VIDEO_OUTBOUND_TIMEOUT_MS,
          minPositiveSamples: 2,
          timeoutMessage: this.sdkI18n.t('errors.systemOverloaded'),
        }))
        await this.ensureBootstrapActive()
      }
    }

    const initialContext = normalizeRealtimeContext(options.context ?? { prompt: '' })

    this.currentContext = initialContext

    if (this.localElement && prepared.internalCamera) {
      queueMicrotask(() => this.rtc.refreshLocalVideoPreview())
    }

    const autoStart = options.autoStart ?? DEFAULT_REALTIME_AUTO_START
    if (autoStart) {
      await this.runStart({
        prompt: this.currentContext.prompt,
        refImage: this.currentContext.refImageUrl ?? undefined,
      })
      if (this.appleMobileVideoUpload) {
        await this.ensureBootstrapActive()
      }
      this.runningContext = this.currentContext
      this.setState('running')
    }

  }

  /** Replace the provisional camera source metadata with the actual RTC capture result. */
  private updateCameraSourceSetting(
    settings: MediaTrackSettings,
    shouldNotify = true,
  ): void {
    const previous = this.media.sourceSetting
    const sourceSetting = Object.freeze({
      width: isPositiveFinite(settings.width) ? settings.width : previous.width,
      height: isPositiveFinite(settings.height) ? settings.height : previous.height,
      fps: isPositiveFinite(settings.frameRate) ? settings.frameRate : previous.fps,
    })
    this.mediaValue = Object.freeze({
      ...this.media,
      sourceSetting,
    })
    if (shouldNotify) {
      this.notifyLowInputResolution('camera', sourceSetting)
    }
  }

  /** Emit one non-fatal source-resolution notice per Session. */
  private notifyLowInputResolution(
    inputKind: RealtimeMediaKind,
    setting: Pick<RealtimeMediaSetting, 'width' | 'height'>,
  ): void {
    if (this.lowInputResolutionNotified) {
      return
    }

    const notice = createLowInputResolutionNotice({
      inputKind,
      width: setting.width,
      height: setting.height,
      message: this.sdkI18n.t(
        inputKind === 'image'
          ? 'notices.lowInputImageResolution'
          : 'notices.lowInputResolution',
      ),
    })
    if (!notice) {
      return
    }

    this.lowInputResolutionNotified = true
    notifyNotice(notice, this.notifyUserNotice)
  }

  /** Start billing heartbeat once, after the client renders the first remote output frame. */
  private startHeartbeatAfterFirstRemoteFrame(): void {
    if (this.heartbeatStarted || this.disconnected || !this.sessionUid || !this.taskUid) {
      return
    }
    this.heartbeatStarted = true
    this.client.startHeartbeat(
      this.sessionUid,
      this.heartbeatIntervalMs,
      (heartbeatSession, diagnostics) => {
        if (heartbeatSession.status !== ACTIVE_STATUS) {
          const allowedReasons = new Set(['idle_timeout', 'duration_limit', 'token_expired', 'quota_exhausted', 'user_closed', 'server_error', 'session_expired'])
          const rawReason = heartbeatSession.closeReason
          const closeTime = heartbeatSession.closeTimestamp ? Date.parse(heartbeatSession.closeTimestamp) : NaN
          void this.safeDisconnect({
            reason: 'session_inactive',
            diagnostics: {
              ...diagnostics, session_status: heartbeatSession.status,
              server_close_reason: rawReason ? (allowedReasons.has(rawReason) ? rawReason : 'unrecognized') : undefined,
              server_close_reason_present: Boolean(rawReason),
              server_close_timestamp_ms: Number.isFinite(closeTime) ? closeTime : undefined,
              failure_stage: 'heartbeat',
            },
          })
        }
      },
      (error, diagnostics) => {
        void this.safeDisconnect({
          reason: 'heartbeat_error',
          diagnostics: { ...diagnosticError(error), ...diagnostics, failure_stage: 'heartbeat' },
        })
      },
    )
  }

  /** Forward RTC's native first-rendered-frame signal without coupling consumers to the player DOM. */
  private handleRemoteVideoFirstFrame(info: RealtimeRemoteVideoFirstFrameInfo): void {
    if (this.disconnected || !this.taskUid || !this.initialRemoteFramePending) {
      return
    }
    this.initialRemoteFramePending = false
    this.startup?.firstFrame({
      output_width: info.width,
      output_height: info.height,
    })
    this.rtc.clearRemoteFirstOutputWatch()
    this.startHeartbeatAfterFirstRemoteFrame()
    if (this.remoteFirstFrameNotified) {
      return
    }
    this.remoteFirstFrameNotified = true
    this.rtc.setOnRemoteVideoFirstFrame(undefined)
    try {
      this.onRemoteVideoFirstFrame?.(info)
    } catch {
      // Consumer callbacks must not break the RTC or Session lifecycle.
    }
  }

  private setState(state: RealtimeSessionState): void {
    if (this.currentState === state) {
      return
    }
    this.currentState = state
    this.startup?.record('sdk_session_state_change', { state })
    try {
      this.onStateChange?.(state)
    } catch {
      // Consumer callbacks must not break the session lifecycle.
    }
  }

  private assertConnected(): void {
    if (this.currentState === 'disconnected') {
      throw new XmaxSdkError('SESSION_ERROR', 'Realtime session is disconnected')
    }
  }

  async start(context?: RealtimeContext): Promise<void> {
    return this.startWithInternalOptions(context)
  }

  async set(context: RealtimeContext): Promise<void> {
    this.assertConnected()

    const nextContext = normalizeRealtimeContext({
      ...this.currentContext,
      ...context,
      ...(context.refImageUrl === null ? { refImageUrl: undefined } : {}),
    })
    const changesInstant = this.presentation.instant || this.instantUploadAbort || this.instantImage(nextContext)
    this.cancelInstant()
    if (changesInstant) {
      // Preserve explicit null: re-merging a normalized context would restore the old reference.
      return this.startWithInternalOptions(context)
    }
    let revision = this.generationRevision
    const operationId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
    const startsGeneration = this.currentState !== 'running'
    const operationContext = {
      operation_id: operationId,
      generation_id: startsGeneration ? null : this.taskUid ?? null,
    }
    const setStartedAt = performance.now()
    const contextFields = Object.keys(context)
      .filter(key => ['prompt', 'refImageUrl'].includes(key))
      .join(',')
    const uploadedAsset = findUploadedAsset(nextContext.refImageUrl)
    this.startup?.record('sdk_context_set_request', {
      ...operationContext,
      context_fields: contextFields,
      asset_operation_id: uploadedAsset?.operationId,
      asset_age_ms: uploadedAsset ? Math.max(0, performance.now() - uploadedAsset.completedAtMonotonicMs) : undefined,
    })

    if (!startsGeneration) {
      try {
        await this.measureStartup('context_set', () => this.runChangeCondition({
          prompt: nextContext.prompt,
          refImage: nextContext.refImageUrl ?? undefined,
        }))
        if (this.disconnected || revision !== this.generationRevision) return
        this.currentContext = nextContext
        this.runningContext = nextContext
        this.setState('running')
        this.startup?.record('sdk_context_set_response', {
          ...operationContext,
          ok: true,
          duration_ms: Math.round(performance.now() - setStartedAt),
        })
      } catch (error) {
        if (this.disconnected || revision !== this.generationRevision) return
        this.startup?.record('sdk_context_set_response', {
          ...operationContext,
          ok: false,
          duration_ms: Math.round(performance.now() - setStartedAt),
          ...diagnosticError(error),
        })
        await this.failOutputAndDisconnect(error, undefined, operationContext)
      }
      return
    }

    try {
      const pendingStart = this.runStart({
        prompt: nextContext.prompt,
        refImage: nextContext.refImageUrl ?? undefined,
      }, taskUid => {
        operationContext.generation_id = taskUid
      })
      revision = this.generationRevision
      await pendingStart
      if (this.disconnected || revision !== this.generationRevision) return
      this.currentContext = nextContext
      this.runningContext = nextContext
      this.setState('running')
      this.startup?.record('sdk_context_set_response', {
        ...operationContext,
        ok: true,
        duration_ms: Math.round(performance.now() - setStartedAt),
      })
    } catch (error) {
      if (this.disconnected || revision !== this.generationRevision) return
      this.startup?.record('sdk_context_set_response', {
        ...operationContext,
        ok: false,
        duration_ms: Math.round(performance.now() - setStartedAt),
        ...diagnosticError(error),
      })
      throw error
    }
  }

  async startWithInternalOptions(
    context?: RealtimeContext,
    options: InternalRealtimeStartOptions = {},
  ): Promise<void> {
    this.assertConnected()
    this.cancelInstant()
    const nextContext = context
      ? normalizeRealtimeContext({
          ...this.currentContext,
          ...context,
          ...(context.refImageUrl === null ? { refImageUrl: undefined } : {}),
        })
      : this.currentContext

    let revision = this.generationRevision
    try {
      const pendingStart = this.runStart({
        prompt: nextContext.prompt,
        refImage: nextContext.refImageUrl ?? undefined,
        preserveRemoteOutput: options.preserveRemoteOutput,
      })
      revision = this.generationRevision
      await pendingStart
      if (this.disconnected || revision !== this.generationRevision) return
      this.currentContext = nextContext
      this.runningContext = nextContext
      this.setState('running')
    } catch (error) {
      if (this.disconnected || revision !== this.generationRevision) return
      await this.failOutputAndDisconnect(error)
    }
  }

  async sendTracks(tracks: Array<[number, number]>): Promise<void> {
    if (!this.rtc.snapshot.joined || !this.taskUid) {
      return
    }

    try {
      await this.rtc.sendRoomEvent(
        createTracksRtcRoomEvent({
          userId: this.rtc.snapshot.userId ?? undefined,
          uid: this.taskUid,
          sessionUid: this.taskUid,
          tracks,
        }),
      )
    } catch {
      // Track sending is best-effort; failures are ignored.
    }
  }

  async stopGeneration(): Promise<void> {
    return this.stopInternal({ trigger: 'client_api' })
  }

  async stopLocalStream(): Promise<void> {
    this.cancelInstant()
    const startedAt = performance.now()
    if (this.disconnected) {
      this.startup?.setTerminalContext({
        local_stream_stop_called: true,
        local_stream_stop_outcome: 'noop',
        local_stream_stop_trigger: 'session_already_disconnected',
        local_stream_stop_offset_ms: this.startup.offset(),
      })
      return
    }
    const wasPublished = this.rtc.snapshot.localVideoPublished
    let publishStopSucceeded = true
    let cleanupSucceeded = true
    let stopError: unknown
    if (!this.mediaCleaned) {
      this.mediaCleaned = true
      try {
        this.cleanupMedia?.()
      } catch (error) {
        cleanupSucceeded = false
        stopError = error
      }
    }
    try {
      await this.rtc.stopVideoPublishing({
        preserveExternalTrack: !this.ownsMedia,
        preserveRemote: true,
      })
    } catch (error) {
      publishStopSucceeded = false
      stopError = error
    }
    this.startup?.setTerminalContext({
      local_stream_stop_called: true,
      local_stream_stop_outcome: publishStopSucceeded && cleanupSucceeded ? 'success' : 'error',
      local_stream_stop_trigger: 'client_api',
      local_stream_stop_offset_ms: this.startup.offset(),
      local_stream_was_published: wasPublished,
      local_stream_publish_stop_succeeded: publishStopSucceeded,
      local_stream_media_cleanup_succeeded: cleanupSucceeded,
      local_stream_stop_duration_ms: Math.round(performance.now() - startedAt),
      ...(stopError ? diagnosticError(stopError) : {}),
    })
    if (stopError) {
      this.startup?.recordError({
        failure_reason: 'local_stream_stop_failed',
        failure_stage: publishStopSucceeded ? 'media_cleanup' : 'local_publish_stop',
        ...diagnosticError(stopError),
      })
    }
  }

  private async stopInternal(
    options?: {
      preserveRemoteDom?: boolean
      suppressStateChange?: boolean
      trigger?: 'client_api' | 'session_disconnect'
    },
  ): Promise<void> {
    this.assertConnected()
    this.cancelInstant()
    const startedAt = performance.now()
    const hadGeneration = Boolean(this.taskUid)
    const stoppedGenerationId = this.taskUid
    let stopMessageAttempted = false
    this.initialRemoteFramePending = false
    this.rtc.clearRemoteFirstOutputWatch()
    this.rtc.clearLocalOutboundWatch()
    this.updateDragController(false)

    const preserveRemoteDom = options?.preserveRemoteDom ?? false
    if (preserveRemoteDom) {
      this.rtc.setPreserveRemoteDom(true)
    }

    // Generation has stopped; keep RTC publishing alive but stop tagging frames
    // with the completed task id and clear the corresponding remote SEI gate.
    this.rtc.configureSessionSei(null)

    if (!this.rtc.snapshot.joined) {
      this.rtc.clearRemoteVideo({ preserveDom: preserveRemoteDom })
      if (!preserveRemoteDom && this.remoteVideoContainer) {
        this.rtc.setRemoteVideoContainer(null)
      }
      this.taskUid = null
      this.runningContext = null
      this.startup?.clearGeneration(stoppedGenerationId)
      if (!options?.suppressStateChange) {
        this.setState('idle')
      }
      if (preserveRemoteDom) {
        this.rtc.setPreserveRemoteDom(false)
      }
      this.startup?.setTerminalContext({
        generation_stop_called: true,
        generation_stop_outcome: hadGeneration ? 'local_cleanup' : 'noop',
        generation_stop_trigger: options?.trigger ?? 'client_api',
        generation_stop_offset_ms: this.startup.offset(),
        generation_stop_had_generation: hadGeneration,
        generation_stop_message_attempted: false,
        generation_stop_duration_ms: Math.round(performance.now() - startedAt),
      })
      return
    }

    try {
      stopMessageAttempted = true
      await this.rtc.sendRoomEvent(
        createStopRtcRoomEvent({
          userId: this.rtc.snapshot.userId ?? undefined,
          sessionUid: this.taskUid ?? undefined,
        }),
      )
      this.startup?.setTerminalContext({
        generation_stop_called: true,
        generation_stop_outcome: 'success',
        generation_stop_trigger: options?.trigger ?? 'client_api',
        generation_stop_offset_ms: this.startup.offset(),
        generation_stop_had_generation: hadGeneration,
        generation_stop_message_attempted: stopMessageAttempted,
        generation_stop_duration_ms: Math.round(performance.now() - startedAt),
      })
    } catch (error) {
      this.startup?.recordError({
        failure_reason: 'generation_stop_failed',
        generation_id: stoppedGenerationId,
        failure_stage: 'generation_stop',
        ...diagnosticError(error),
      })
      this.startup?.setTerminalContext({
        generation_stop_called: true,
        generation_stop_outcome: 'error',
        generation_stop_trigger: options?.trigger ?? 'client_api',
        generation_stop_offset_ms: this.startup.offset(),
        generation_stop_had_generation: hadGeneration,
        generation_stop_message_attempted: stopMessageAttempted,
        generation_stop_duration_ms: Math.round(performance.now() - startedAt),
        ...diagnosticError(error),
      })
      throw error
    } finally {
      this.rtc.clearRemoteVideo({ preserveDom: preserveRemoteDom })
      if (!preserveRemoteDom && this.remoteVideoContainer) {
        this.rtc.setRemoteVideoContainer(null)
      }
      this.taskUid = null
      this.runningContext = null
      this.startup?.clearGeneration(stoppedGenerationId)
      if (!options?.suppressStateChange) {
        this.setState('idle')
      }
      if (preserveRemoteDom) {
        this.rtc.setPreserveRemoteDom(false)
      }
    }
  }

  async disconnect(reason: InternalRealtimeDisconnectReason = 'client_disconnect'): Promise<void> {
    if (this.disconnected) {
      return
    }
    this.disconnected = true
    await this.safeDisconnect({ reason: 'client', failureReason: reason })
  }

  getSessionUid(): string | null {
    return this.sessionUid
  }

  /** Keep change_condition size pinned to the first start event. */
  private resolveChangeConditionSize(): [number, number] {
    if (!this.firstStartEventSize) {
      return [
        this.media.streamSetting.width,
        this.media.streamSetting.height,
      ]
    }
    return [this.firstStartEventSize[0], this.firstStartEventSize[1]]
  }

  private async runStart(options: {
    prompt?: string
    refImage?: string
    preserveRemoteOutput?: boolean
  }, onGenerationCreated?: (taskUid: string) => void): Promise<void> {
    if (!this.rtc.snapshot.joined) {
      return
    }

    const revision = this.generationRevision = (this.generationRevision ?? 0) + 1
    const isCurrent = () => !this.disconnected && this.generationRevision === revision
    const startPrompt = options.prompt ?? this.currentContext.prompt
    const instantSource = this.instantImage({ prompt: startPrompt, refImageUrl: options.refImage })
    let staticImagePath: string | undefined
    if (instantSource) {
      try {
        staticImagePath = await this.prepareInstantImage(instantSource)
      } catch (error) {
        if (!isCurrent()) return
        throw error
      }
      if (!isCurrent()) return
      if (!this.instant) {
        this.instant = new InstantPresentation({
          container: this.hiddenRemoteElement ? undefined : this.remoteViewHost?.wrapper,
          fit: this.dragTrackFitMode,
          transitionMs: this.inputImageTransitionMs,
          onChange: this.onPresentationChange,
          restart: () => this.runStart({
            prompt: this.currentContext.prompt,
            refImage: this.currentContext.refImageUrl ?? undefined,
          }),
        })
      }
      this.instant.activate(instantSource)
    } else {
      this.instant?.reset()
    }
    if (!isCurrent()) return

    let taskUid: string | null = null
    const preserveRemoteOutput = options.preserveRemoteOutput ?? false
    if (preserveRemoteOutput) {
      this.rtc.setPreserveRemoteDom(true)
    }

    try {
      if (!preserveRemoteOutput) {
        this.rtc.setRemoteVideoContainer(null)
        this.rtc.clearRemoteVideo()
      }

      const size: [number, number] = [
        this.media.streamSetting.width,
        this.media.streamSetting.height,
      ]
      if (!this.firstStartEventSize) {
        this.firstStartEventSize = [size[0], size[1]] as const
      }
      taskUid = createTaskUid()
      this.taskUid = taskUid
      this.startup?.setGeneration(taskUid)
      onGenerationCreated?.(taskUid)

      this.rtc.configureSessionSei(taskUid)

      if (this.remoteVideoContainer) {
        this.rtc.setRemoteVideoContainer(this.remoteVideoContainer)
      }

      const startRoomEvent = createStartRtcRoomEvent({
        userId: this.rtc.snapshot.userId ?? undefined,
        uid: taskUid,
        sessionUid: taskUid,
        model: DEFAULT_REALTIME_START_EVENT_MODEL,
        size,
        prompt: startPrompt,
        refImagePath: instantSource ? undefined : options.refImage,
        staticGenerate: instantSource ? true : undefined,
        staticImagePath,
        mirror: this.shouldMirrorCameraInput,
      })

      // Register the initial-frame expectation before sending start so an immediate
      // RTC callback cannot be missed while the send promise is resolving.
      this.initialRemoteFramePending = Boolean(
        this.remoteVideoContainer && !this.remoteFirstFrameNotified,
      )

      await this.runAppleMobileVideoStage(
        () => this.measureStartup(
          'start_send',
          () => this.rtc.sendRoomEvent(startRoomEvent),
        ),
        {
          stage: 'start_event_send',
          timeoutMs: APPLE_MOBILE_VIDEO_START_SEND_TIMEOUT_MS,
        },
      )
      if (!isCurrent()) return
      if (this.appleMobileVideoUpload) {
        await this.ensureBootstrapActive()
      }

      // Only an actually presented initial output completes this deadline. If an
      // immediate frame arrived while sendRoomEvent was resolving, pending is false.
      if (this.initialRemoteFramePending) {
        this.rtc.armRemoteFirstOutputWatch({
          onTimeout: () => {
            if (!this.rtc.confirmRemoteVideoFirstFrame()) {
              this.failOverloaded('remote_first_frame_timeout')
            }
          },
        })
      }

      const isMobileUploadInput =
        (this.media.kind === 'video' || this.media.kind === 'image')
        && isLikelyMobileRuntime()
      this.rtc.armLocalOutboundWatch({
        minZeroSamples: isMobileUploadInput ? 5 : 3,
        minStallMs: isMobileUploadInput ? 12_000 : 6000,
        startGraceMs: isMobileUploadInput ? 12_000 : 6000,
        resumeGraceMs: isMobileUploadInput ? 8000 : 5000,
        onStalled: (info) => {
          this.failOverloaded(localOutboundDisconnectReason(info.reason), {
            detection_source: 'local_outbound_watch', stall_ms: info.stallMs,
            consecutive_zero_samples: info.consecutiveZeroStats, up_fps: info.lastSentFrameRate,
          })
        },
      })

      await this.measureStartup(
        'remote_bind',
        () => this.rtc.refreshRemoteVideoBinding({ force: !preserveRemoteOutput }),
      )
      if (!isCurrent()) return
      this.startup?.enter(this.initialRemoteFramePending ? 'wait_first_frame' : 'streaming')
      if (this.appleMobileVideoUpload) {
        await this.ensureBootstrapActive()
      }
      this.updateDragController(true)
      this.scheduleNotifyRemoteStream()
    } catch (error) {
      if (!isCurrent()) return
      this.initialRemoteFramePending = false
      this.rtc.configureSessionSei(null)
      this.rtc.clearRemoteFirstOutputWatch()
      this.rtc.clearLocalOutboundWatch()
      if (this.disconnected && error instanceof SessionBootstrapCancelledError) {
        throw error
      }
      // Publish / start / bind failure all block output — stop + full disconnect.
      await this.failOutputAndDisconnect(error, undefined, { generation_id: taskUid })
    } finally {
      if (preserveRemoteOutput) {
        this.rtc.setPreserveRemoteDom(false)
      }
    }
  }

  private async runChangeCondition(options: {
    prompt?: string
    refImage?: string
  }): Promise<void> {
    if (!this.rtc.snapshot.joined) {
      return
    }

    const size = this.resolveChangeConditionSize()
    const changeConditionRoomEvent = createChangeConditionRtcRoomEvent({
      userId: this.rtc.snapshot.userId ?? undefined,
      sessionUid: this.taskUid ?? undefined,
      model: DEFAULT_REALTIME_START_EVENT_MODEL,
      size,
      prompt: options.prompt ?? this.currentContext.prompt,
      refImagePath: options.refImage,
    })

    await this.measureTiming(
      '发送切换条件事件',
      () => this.rtc.sendRoomEvent(changeConditionRoomEvent),
      {
        prompt: changeConditionRoomEvent.params.prompt,
        size: changeConditionRoomEvent.params.size,
        hasRefImage: Boolean(changeConditionRoomEvent.params.ref_image_path),
      },
    )
  }

  private isDragEnabled(): boolean {
    return (
      this.dragSetting?.enabled
      ?? DEFAULT_REALTIME_RENDER_SETTING.dragEnabled
    )
      && Boolean(this.remoteElement && !this.hiddenRemoteElement)
  }

  private updateDragController(active: boolean): void {
    const dragHost = this.remoteViewHost?.dragHost
    if (!dragHost || !active || !this.taskUid || !this.isDragEnabled()) {
      this.dragController?.setEnabled(false)
      return
    }

    const targetSize: [number, number] = [
      this.media.streamSetting.width,
      this.media.streamSetting.height,
    ]
    if (!this.dragController) {
      this.dragController = createDragTrackController(dragHost, {
        targetSize,
        fitMode: this.dragTrackFitMode,
        mirrored: false,
        enabled: true,
        onTracks: (tracks) => {
          void this.sendTracks(tracks)
        },
        onStrokeStart: () => {
          this.beginDrag()
          return this.dragSetting?.onStart?.()
        },
        onStrokeEnd: () => {
          this.endDrag()
          return this.dragSetting?.onEnd?.()
        },
      })
      return
    }

    this.dragController.setTargetSize(targetSize)
    this.dragController.setFitMode(this.dragTrackFitMode)
    this.dragController.setMirrored(false)
    this.dragController.setEnabled(true)
  }

  private scheduleNotifyRemoteStream(): void {
    if (!this.onRemoteStream) {
      return
    }

    this.clearRemoteStreamNotifyTimer()
    if (this.notifyRemoteStreamOnce()) {
      return
    }

    let attempts = 0
    this.remoteStreamNotifyTimer = setInterval(() => {
      attempts += 1
      if (this.notifyRemoteStreamOnce() || attempts >= 60) {
        this.clearRemoteStreamNotifyTimer()
      }
    }, 500)
  }

  private notifyRemoteStreamOnce(): boolean {
    const videoRoot = this.remoteVideoContainer
    if (!this.onRemoteStream || !videoRoot) {
      return false
    }

    const video = videoRoot instanceof HTMLVideoElement
      ? videoRoot
      : videoRoot.querySelector('video')

    if (!(video instanceof HTMLVideoElement) || !(video.srcObject instanceof MediaStream)) {
      return false
    }

    if (this.lastNotifiedRemoteStream === video.srcObject) {
      return true
    }

    this.lastNotifiedRemoteStream = video.srcObject
    this.onRemoteStream(video.srcObject)
    return true
  }

  private clearRemoteStreamNotifyTimer(): void {
    if (this.remoteStreamNotifyTimer !== null) {
      clearInterval(this.remoteStreamNotifyTimer)
      this.remoteStreamNotifyTimer = null
    }
  }

  private emitDisconnect(reason: RealtimeDisconnectReason): void {
    if (!this.sessionEstablished || this.disconnectNotified) {
      return
    }
    this.disconnectNotified = true
    try {
      const recorded = this.startup?.terminalFailureDetails
      if (recorded?.failure_reason && this.disconnectDetails
        && ['client_disconnect', 'client_background_timeout', 'client_session_replaced', 'client_context_disposed', 'pagehide', 'session_error'].includes(this.disconnectDetails.reason_code ?? '')) {
        this.disconnectDetails = createErrorDetails({ ...this.disconnectDetails, ...recorded, reason_code: recorded.failure_reason })
      }
      this.onDisconnect?.(reason, this.disconnectDetails ?? this.diagnosticDetails(probeDisconnectReason(reason)))
    } catch {
      // `onDisconnect` errors must not break teardown.
    }
  }

  private async safeDisconnect(
    options?: {
      reason?: RealtimeDisconnectReason
      failureReason?: XmaxDisconnectReasonCode
      diagnostics?: XmaxTelemetryParams
    },
  ): Promise<void> {
    this.cancelInstant()
    const startedAt = performance.now()
    const reason = options?.reason ?? this.pendingDisconnectReason
    const failureReason = options?.failureReason ?? probeDisconnectReason(reason)
    this.disconnectDetails ??= this.diagnosticDetails(failureReason, options?.diagnostics)
    const { reason_code: _reason, trace_id: _trace, session_id: _session, generation_id: _generation, ...details } = this.disconnectDetails
    options = { ...options, diagnostics: { ...options?.diagnostics, ...details } }
    if (reason !== 'client') {
      this.startup?.recordError({ ...options?.diagnostics, failure_reason: failureReason })
    }
    this.pendingDisconnectReason = reason
    this.disconnected = true
    this.initialRemoteFramePending = false
    this.rtc.beginTeardown()

    this.rtc.clearRemoteFirstOutputWatch()
    this.rtc.clearLocalOutboundWatch()
    this.clearRemoteStreamNotifyTimer()
    this.lastNotifiedRemoteStream = null
    const activeSessionUid = this.sessionUid
    this.sessionUid = null
    const teardownFailures: string[] = []
    let firstTeardownError: unknown
    let generationStopAttempted = false
    let generationStopSucceeded = false
    let publishStopAttempted = false
    let publishStopSucceeded = false
    let rtcLeaveSucceeded = false
    const serverCloseAttempted = Boolean(activeSessionUid)
    let serverCloseConfirmed = false
    let mediaCleanupSucceeded = true

    this.client.beginTeardown()

    // Stop capture immediately; room messaging remains available until leave.
    publishStopAttempted = this.rtc.snapshot.joined
    const publishStopResult = publishStopAttempted
      ? this.rtc.stopVideoPublishing({ preserveExternalTrack: !this.ownsMedia })
        .then(() => ({ ok: true as const }), error => ({ ok: false as const, error }))
      : Promise.resolve({ ok: true as const })

    // Send Stop while still joined, then tear down room / HTTP session.
    const generationActive = !!this.taskUid && this.rtc.snapshot.joined
    try {
      if (generationActive) {
        generationStopAttempted = true
        await this.stopInternal({
          suppressStateChange: true,
          trigger: 'session_disconnect',
        })
        generationStopSucceeded = true
      } else {
        this.updateDragController(false)
        this.rtc.clearRemoteVideo()
        if (this.remoteVideoContainer) {
          this.rtc.setRemoteVideoContainer(null)
        }
        this.taskUid = null
      }
    } catch (error) {
      generationStopSucceeded = false
      teardownFailures.push('generation_stop')
      firstTeardownError ??= error
    }

    try {
      const result = await publishStopResult
      if (!result.ok) throw result.error
      publishStopSucceeded = publishStopAttempted
    } catch (error) {
      publishStopSucceeded = false
      teardownFailures.push('local_publish_stop')
      firstTeardownError ??= error
      this.startup?.recordError({
        failure_reason: 'local_stream_stop_failed',
        failure_stage: 'local_publish_stop',
        ...diagnosticError(error),
      })
    }
    try {
      await this.rtc.leave({ preserveExternalTrack: !this.ownsMedia })
      rtcLeaveSucceeded = true
    } catch (error) {
      rtcLeaveSucceeded = false
      teardownFailures.push('rtc_leave')
      firstTeardownError ??= error
      this.startup?.recordError({
        failure_reason: 'rtc_leave_failed',
        failure_stage: 'rtc_leave',
        ...diagnosticError(error),
      })
    }

    if (activeSessionUid) {
      try {
        serverCloseConfirmed = (await this.client.closeSession(activeSessionUid)) !== null
        if (!serverCloseConfirmed) {
          teardownFailures.push('server_close')
          this.startup?.recordError({
            failure_reason: 'session_close_failed',
            failure_stage: 'server_close',
            failure_type: 'invalid_response',
          })
        }
      } catch (error) {
        serverCloseConfirmed = false
        teardownFailures.push('server_close')
        firstTeardownError ??= error
        this.startup?.recordError({
          failure_reason: 'session_close_failed',
          failure_stage: 'server_close',
          ...diagnosticError(error),
        })
      }
    }

    try {
      this.client.endTeardown()
    } catch (error) {
      teardownFailures.push('client_teardown_end')
      firstTeardownError ??= error
      this.startup?.recordError({
        failure_reason: 'session_close_failed',
        failure_stage: 'client_teardown_end',
        ...diagnosticError(error),
      })
    }

    this.taskUid = null
    try {
      this.dragController?.destroy()
    } catch (error) {
      teardownFailures.push('drag_cleanup')
      firstTeardownError ??= error
      this.startup?.recordError({
        failure_reason: 'media_cleanup_failed',
        failure_stage: 'drag_cleanup',
        ...diagnosticError(error),
      })
    }
    this.dragController = null
    this.renderCleanup.splice(0).forEach((cleanup) => {
      try {
        cleanup()
      } catch (error) {
        teardownFailures.push('render_cleanup')
        firstTeardownError ??= error
        this.startup?.recordError({
          failure_reason: 'media_cleanup_failed',
          failure_stage: 'render_cleanup',
          ...diagnosticError(error),
        })
      }
    })
    if (!this.mediaCleaned) {
      this.mediaCleaned = true
      try {
        this.cleanupMedia?.()
      } catch (error) {
        mediaCleanupSucceeded = false
        teardownFailures.push('media_cleanup')
        firstTeardownError ??= error
        this.startup?.recordError({
          failure_reason: 'media_cleanup_failed',
          failure_stage: 'media_cleanup',
          ...diagnosticError(error),
        })
      }
    }

    if (this.hiddenRemoteElement) {
      try {
        this.hiddenRemoteElement.remove()
      } catch (error) {
        teardownFailures.push('remote_element_cleanup')
        firstTeardownError ??= error
        this.startup?.recordError({
          failure_reason: 'media_cleanup_failed',
          failure_stage: 'remote_element_cleanup',
          ...diagnosticError(error),
        })
      }
      this.hiddenRemoteElement = null
    }

    this.runningContext = null
    this.startup?.clearGeneration()
    this.setState('disconnected')
    this.disconnectDetails = createErrorDetails({ ...this.disconnectDetails,
      teardown_failure_stages: teardownFailures.length ? teardownFailures.join(',') : undefined })
    this.emitDisconnect(reason)
    this.startup?.finish(reason === 'client' && failureReason !== 'session_error' ? 'cancelled' : 'error', {
      ...options?.diagnostics,
      failure_reason: failureReason,
      close_mode: 'awaited',
      generation_stop_attempted: generationStopAttempted,
      generation_stop_succeeded: generationStopSucceeded,
      local_publish_stop_attempted: publishStopAttempted,
      local_publish_stop_succeeded: publishStopSucceeded,
      rtc_leave_attempted: true,
      rtc_leave_succeeded: rtcLeaveSucceeded,
      server_close_attempted: serverCloseAttempted,
      server_close_confirmed: serverCloseConfirmed,
      media_cleanup_succeeded: mediaCleanupSucceeded,
      teardown_failure_stages: teardownFailures.length ? teardownFailures.join(',') : undefined,
      duration_ms: Math.round(performance.now() - startedAt),
      ...(firstTeardownError ? { teardown_error_code: diagnosticError(firstTeardownError).error_code, teardown_failure_type: diagnosticError(firstTeardownError).failure_type } : {}),
    })
  }
}

export function disconnectRealtimeSessionInternal(
  session: RealtimeSession,
  reason: InternalRealtimeDisconnectReason,
): Promise<void> {
  if (!(session instanceof RealtimeSessionImpl)) {
    throw new XmaxSdkError('INVALID_INPUT', 'Expected an SDK RealtimeSession')
  }
  return session.disconnect(reason)
}

export function startRealtimeSessionInternal(
  session: RealtimeSession,
  options: InternalRealtimeStartOptions,
): Promise<void> {
  if (!(session instanceof RealtimeSessionImpl)) {
    throw new XmaxSdkError('INVALID_INPUT', 'Expected an SDK RealtimeSession')
  }
  return session.startWithInternalOptions(undefined, options)
}
